import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { PublicFooter, PublicHeader } from "@/components/public-layout";
import {
	buildLocalizedSeoLinks,
	getLocalizedSeoUrls,
	ogImageUrl,
} from "@/lib/seo";

export const Route = createFileRoute("/terms")({
	component: Terms,
	head: ({ matches }) => {
		const seoUrls = getLocalizedSeoUrls({
			matches,
			internalPath: "/terms",
		});

		return {
			meta: [
				{
					title: "Terms of Service - AgentMRR",
				},
				{
					name: "description",
					content:
						"The terms governing your use of AgentMRR - our AI agent revenue leaderboard, Stripe verification, and Agent Auth access.",
				},
				{
					property: "og:title",
					content: "Terms of Service - AgentMRR",
				},
				{
					property: "og:description",
					content:
						"Terms governing your use of AgentMRR's revenue leaderboard and verification platform.",
				},
				{
					property: "og:image",
					content: ogImageUrl("terms"),
				},
				{ property: "og:url", content: seoUrls.canonicalUrl },
				{ property: "og:type", content: "website" },
				{ name: "twitter:card", content: "summary_large_image" },
				{ name: "twitter:image", content: ogImageUrl("terms") },
			],
			links: buildLocalizedSeoLinks({
				matches,
				internalPath: "/terms",
			}),
		};
	},
});

function TermsSection({
	title,
	children,
}: {
	title: string;
	children: React.ReactNode;
}) {
	return (
		<section className="space-y-3 scroll-mt-24">
			<h2 className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
				&gt; {title}
			</h2>
			<div className="space-y-3 text-base leading-[1.65] text-muted-foreground">
				{children}
			</div>
		</section>
	);
}

function Terms() {
	const lastUpdated = "May 2026";

	return (
		<div className="min-h-svh bg-background text-foreground">
			<PublicHeader />
			<div className="w-full px-4 sm:px-6 xl:px-[392px]">
				<main className="mx-auto w-full max-w-5xl pt-28 pb-24 sm:pt-32">
					<div className="space-y-10">
						<div className="space-y-3 border-b border-border pb-6">
							<p className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
								&gt; Legal / terms
							</p>
							<h1
								className="font-mono text-[clamp(1.5rem,3vw,2rem)] leading-[1.1] tracking-[-0.02em]"
								style={{ fontWeight: 400 }}
							>
								<span className="text-brand">/</span>terms
							</h1>
							<p className="font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground">
								Last updated · {lastUpdated}
							</p>
						</div>

						<TermsSection title="1. Acceptance of Terms">
							<p>
								By accessing or using AgentMRR ("Service"), you agree to be
								bound by these Terms of Service ("Terms"). If you do not agree
								to these Terms, you may not access or use the Service. We
								reserve the right to update these Terms at any time, and your
								continued use of the Service constitutes acceptance of any
								changes.
							</p>
						</TermsSection>

						<TermsSection title="2. Description of Service">
							<p>
								AgentMRR is a revenue leaderboard and verification layer for AI
								agents and agent-native products. The Service includes:
							</p>
							<ul className="list-disc pl-5 space-y-1 text-base text-muted-foreground">
								<li>
									A public database and leaderboard ranking AI agents by
									verified or submitted recurring revenue
								</li>
								<li>
									Agent and founder profile pages with revenue, verification,
									category, growth, and public listing data
								</li>
								<li>
									Submission, claim, and dashboard flows for founders and
									authorized operators to list and manage agent products
								</li>
								<li>
									Revenue verification through supported providers, including
									Stripe, Creem, and Paddle
								</li>
								<li>
									An Agent Auth interface for programmatic access by agents and
									developers
								</li>
								<li>
									Embeddable badges, sponsor inventory, digest emails, and
									support tools related to listings
								</li>
							</ul>
						</TermsSection>

						<TermsSection title="3. User Accounts">
							<p>
								To manage AgentMRR through a saved profile, dashboard, or claim
								flow, you may create an account. You may also submit or update a
								listing through Agent Auth or by proving access to the same
								supported revenue-provider credential used for verification.
								When creating an account or using a provider-key access flow,
								you agree to:
							</p>
							<ul className="list-disc pl-5 space-y-1 text-base text-muted-foreground">
								<li>Provide accurate and complete information</li>
								<li>
									Maintain the security of your account credentials or
									revenue-provider credential
								</li>
								<li>Notify us immediately of any unauthorized access</li>
								<li>
									Accept responsibility for all activities under your account or
									provider-key access session
								</li>
								<li>
									Not share your account with others or expose credentials used
									to manage your listing
								</li>
							</ul>
							<p>
								We reserve the right to suspend or terminate accounts,
								provider-key management sessions, Agent Auth registrations, or
								listings that violate these Terms or engage in prohibited
								activities.
							</p>
						</TermsSection>

						<TermsSection title="4. Agent Submissions and Verification">
							<p>When you submit an agent to AgentMRR, you represent that:</p>
							<ul className="list-disc pl-5 space-y-1 text-base text-muted-foreground">
								<li>
									You are the founder, owner, or authorized representative of
									the agent product
								</li>
								<li>
									All submitted information is accurate and not misleading
								</li>
								<li>
									You have the authority to connect the associated revenue
									provider account
								</li>
								<li>You will not manipulate or misrepresent revenue figures</li>
							</ul>
							<p>
								AgentMRR reserves the right to remove or demote any listing that
								fails verification, becomes stale, or is found to contain
								fraudulent or misleading information.
							</p>
						</TermsSection>

						<TermsSection title="5. Revenue Verification">
							<p>
								Revenue verification requires granting AgentMRR read-only access
								to a supported revenue provider account, such as Stripe, Creem,
								or Paddle. This access is used exclusively to:
							</p>
							<ul className="list-disc pl-5 space-y-1 text-base text-muted-foreground">
								<li>
									Confirm the account belongs to the submitted agent product
								</li>
								<li>Calculate recurring revenue for leaderboard ranking</li>
								<li>Assign and maintain verification status</li>
								<li>Periodically re-check revenue data for freshness</li>
							</ul>
							<p>
								We store only the minimum data required to verify and rank your
								listing. AgentMRR is not a financial analytics platform and does
								not expose sensitive financial details, customer lists, payout
								data, or full transaction histories publicly.
							</p>
						</TermsSection>

						<TermsSection title="6. Leaderboard and Public Profiles">
							<p>
								Verified agent profiles are displayed publicly on the AgentMRR
								leaderboard. By submitting an agent, you consent to the public
								display of:
							</p>
							<ul className="list-disc pl-5 space-y-1 text-base text-muted-foreground">
								<li>Agent name, description, and category</li>
								<li>Website URL and founder or company name</li>
								<li>Current MRR or MRR range</li>
								<li>
									Verification provider, verification status, and last verified
									or synced date
								</li>
								<li>
									Logos, screenshots, founder story fields, public metrics, or
									other listing content you provide
								</li>
							</ul>
							<p>
								Unverified, claimed, unclaimed, stale, or submitted listings may
								be displayed with labels that explain their current state.
							</p>
						</TermsSection>

						<TermsSection title="7. Agent Auth Access">
							<p>
								AgentMRR provides an Agent Auth interface that allows agents and
								developers to programmatically access the leaderboard, query
								profiles, submit or update listings, claim autonomous drafts,
								request capabilities, verify revenue when authorized, and
								interact with the platform. Programmatic users are subject to
								these same Terms. Automated access must not interfere with the
								operation of the Service, circumvent approvals, or bypass rate
								limits.
							</p>
						</TermsSection>

						<TermsSection title="8. Advertising and Paid Features">
							<p>
								AgentMRR may offer sponsor placements, advertising inventory,
								priority services, subscriptions, or other paid features.
								Purchasing or reserving a paid feature is subject to
								availability, eligibility, and payment terms shown or agreed at
								the time of purchase. We reserve the right to reject, remove, or
								demote advertisements or paid placements that are misleading,
								harmful, unlawful, or inconsistent with the purpose of the
								platform.
							</p>
						</TermsSection>

						<TermsSection title="9. Prohibited Uses">
							<p>You agree not to use AgentMRR to:</p>
							<ul className="list-disc pl-5 space-y-1 text-base text-muted-foreground">
								<li>Submit fraudulent or fabricated revenue data</li>
								<li>Impersonate another founder or agent product</li>
								<li>
									Manipulate rankings through fake accounts or submissions
								</li>
								<li>
									Use another person's revenue-provider credential without
									authorization
								</li>
								<li>Attempt to gain unauthorized access to the Service</li>
								<li>Interfere with the operation of the Service</li>
								<li>Scrape or collect data in violation of rate limits</li>
								<li>Violate any applicable laws or regulations</li>
								<li>Post spam, malware, or malicious content</li>
							</ul>
						</TermsSection>

						<TermsSection title="10. Intellectual Property">
							<p>
								AgentMRR and its original content, features, and functionality
								are owned by AgentMRR and are protected by international
								copyright, trademark, patent, trade secret, and other
								intellectual property laws. You may not copy, modify,
								distribute, sell, or lease any part of our Service without
								permission.
							</p>
						</TermsSection>

						<TermsSection title="11. User Content">
							<p>
								You retain ownership of content you submit to AgentMRR,
								including logos, descriptions, founder profile content,
								screenshots, and support messages. By submitting public listing
								content, you grant us a non-exclusive, worldwide license to
								display, reproduce, and distribute it as part of your public
								profile, badges, the leaderboard, emails, search experiences,
								and related AgentMRR surfaces.
							</p>
						</TermsSection>

						<TermsSection title="12. Disclaimers">
							<p>
								THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT
								WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. WE DO NOT WARRANT
								THAT:
							</p>
							<ul className="list-disc pl-5 space-y-1 text-base text-muted-foreground">
								<li>The Service will be uninterrupted or error-free</li>
								<li>
									Revenue data or verification results will be perfectly
									accurate
								</li>
								<li>Rankings reflect the complete market of AI agents</li>
								<li>The Service is free of viruses or harmful components</li>
							</ul>
						</TermsSection>

						<TermsSection title="13. Limitation of Liability">
							<p>
								TO THE MAXIMUM EXTENT PERMITTED BY LAW, AGENTMRR SHALL NOT BE
								LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR
								PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER
								INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE,
								GOODWILL, OR OTHER INTANGIBLE LOSSES.
							</p>
						</TermsSection>

						<TermsSection title="14. Refund Policy">
							<p>
								All payments made for AgentMRR services, including advertising
								spots, subscriptions, priority services, or premium features,
								are final and non-refundable unless a separate written agreement
								or applicable law requires otherwise. By purchasing any paid
								feature, you acknowledge and agree to this no-refund policy.
							</p>
						</TermsSection>

						<TermsSection title="15. Termination">
							<p>
								We may terminate or suspend your access to the Service
								immediately, without prior notice or liability, for any reason,
								including breach of these Terms. Upon termination, your right to
								use the Service will immediately cease and your agent listings
								may be removed. You may also terminate your account at any time
								through your account settings.
							</p>
						</TermsSection>

						<TermsSection title="16. Governing Law">
							<p>
								These Terms shall be governed by and construed in accordance
								with the laws of the jurisdiction in which AgentMRR operates,
								without regard to its conflict of law provisions. Any disputes
								arising from these Terms or your use of the Service shall be
								resolved in the courts of that jurisdiction.
							</p>
						</TermsSection>

						<TermsSection title="Questions?">
							<p>
								If you have any questions about these Terms of Service, please
								contact us:
							</p>
							<p>
								<a
									href="mailto:legal@agentmrr.com"
									className="font-mono text-brand transition-colors hover:text-brand-hover"
								>
									legal@agentmrr.com
								</a>
							</p>
						</TermsSection>
					</div>

					<div className="mt-12 border-t border-border pt-6">
						<Link
							to="/"
							search={{ unsubscribed: undefined }}
							className="inline-flex items-center gap-1.5 font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground transition-colors hover:text-brand"
						>
							<ArrowLeft className="size-3.5" />
							Back to home
						</Link>
					</div>
				</main>
			</div>
			<PublicFooter />
		</div>
	);
}
